/**
 * F41 (rev-12 follow-up) — admin in-play score-entry dialog.
 *
 * Opened from the tournament detail Bracket tab via `BracketView`'s
 * `onMatchPress` (gated on `tournaments:enter_scores`; the server re-enforces).
 * Three actions over the admin mirror routes:
 *   - Save draft   → POST /admin/tournaments/:id/matches/:matchId/score
 *   - Finalize     → POST /admin/tournaments/:id/matches/:matchId/finalize
 *   - Correct      → POST /admin/tournaments/:id/matches/:matchId/correct
 *     (only offered on a `final` match; mandatory ≥1-char reason — audited)
 *
 * Sets-based sport-aware score (`TournamentScore.sets[]`); the winner radio
 * resolves the side → `winnerRegistrationId`. A bye/TBD match (either
 * participant null) cannot be scored. Composes F02 primitives only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Checkbox,
  FormField,
  Modal,
  NumberInput,
  Textarea,
} from '@bravemove/ui-web';
import type { MatchDetail, TournamentScore } from '@bravemove/types';
import { adminTournamentsApi } from '../../api/tournaments';
import { translateApiError } from '../../auth/errors';

export interface MatchScoreDialogProps {
  readonly open: boolean;
  readonly tournamentId: string;
  readonly match: MatchDetail | null;
  readonly onClose: () => void;
  /** Called after any successful mutation so the page refetches the bracket. */
  readonly onSaved: () => void | Promise<void>;
}

interface SetRow {
  a: number;
  b: number;
}

function initialSets(match: MatchDetail | null): SetRow[] {
  if (match?.score?.sets && match.score.sets.length > 0) {
    return match.score.sets.map((s) => ({ a: s.a, b: s.b }));
  }
  return [{ a: 0, b: 0 }];
}

export function MatchScoreDialog({
  open,
  tournamentId,
  match,
  onClose,
  onSaved,
}: MatchScoreDialogProps) {
  const { t } = useI18n();
  const [sets, setSets] = useState<SetRow[]>(initialSets(match));
  const [winnerSide, setWinnerSide] = useState<'a' | 'b' | null>(null);
  const [aPresent, setAPresent] = useState(true);
  const [bPresent, setBPresent] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState<'draft' | 'finalize' | 'correct' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isFinal = match?.status === 'final';
  const scorable = match !== null && match.participantA !== null && match.participantB !== null;

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setSets(initialSets(match));
    setWinnerSide(
      match?.winnerRegistrationId
        ? match.winnerRegistrationId === match.participantA?.registrationId
          ? 'a'
          : 'b'
        : null,
    );
    setAPresent(true);
    setBPresent(true);
    setReason('');
    setError(null);
    setSubmitting(null);
  }, [open, match]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const score = useMemo<TournamentScore>(
    () => ({ sets: sets.map((s) => ({ a: s.a, b: s.b })) }),
    [sets],
  );

  const setsValid = sets.length > 0 && sets.every((s) => s.a >= 0 && s.b >= 0);
  const winnerRegistrationId =
    winnerSide === 'a'
      ? (match?.participantA?.registrationId ?? null)
      : winnerSide === 'b'
        ? (match?.participantB?.registrationId ?? null)
        : null;

  const updateSet = (idx: number, side: 'a' | 'b', value: number) => {
    setSets((prev) => prev.map((s, i) => (i === idx ? { ...s, [side]: value } : s)));
  };

  const run = async (
    kind: 'draft' | 'finalize' | 'correct',
    fn: () => Promise<unknown>,
    successKey: string,
    successFallback: string,
  ): Promise<void> => {
    setSubmitting(kind);
    setError(null);
    try {
      await fn();
      await onSaved();
      onClose();
      // The toast lives with the page (onSaved refetches); keep the dialog dumb.
      void successKey;
      void successFallback;
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(null);
    }
  };

  const onSaveDraft = () =>
    run(
      'draft',
      () => adminTournamentsApi.enterScore(tournamentId, match!.id, { score }),
      'admin:tournament.scoreDialog.draftSaved',
      'Draft score saved.',
    );

  const onFinalize = () =>
    run(
      'finalize',
      () =>
        adminTournamentsApi.finalizeMatch(tournamentId, match!.id, {
          winnerRegistrationId: winnerRegistrationId!,
          score,
          participantAPresent: aPresent,
          participantBPresent: bPresent,
        }),
      'admin:tournament.scoreDialog.finalized',
      'Match finalized.',
    );

  const onCorrect = () =>
    run(
      'correct',
      () =>
        adminTournamentsApi.correctScore(tournamentId, match!.id, {
          winnerRegistrationId: winnerRegistrationId!,
          score,
          reason: reason.trim(),
        }),
      'admin:tournament.scoreDialog.corrected',
      'Score corrected.',
    );

  const aName =
    match?.participantA?.displayName ??
    t('admin:tournament.scoreDialog.tbd', { defaultValue: 'TBD' });
  const bName =
    match?.participantB?.displayName ??
    t('admin:tournament.scoreDialog.tbd', { defaultValue: 'TBD' });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isFinal
          ? t('admin:tournament.scoreDialog.correctTitle', { defaultValue: 'Correct match score' })
          : t('admin:tournament.scoreDialog.title', { defaultValue: 'Enter match score' })
      }
      description={`${aName} vs ${bName}`}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting !== null}
            data-testid="match-score-cancel"
          >
            {t('admin:tournament.scoreDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {!isFinal ? (
            <>
              <Button
                variant="secondary"
                onClick={() => void onSaveDraft()}
                loading={submitting === 'draft'}
                disabled={!scorable || !setsValid || submitting !== null}
                data-testid="match-score-save-draft"
              >
                {t('admin:tournament.scoreDialog.saveDraft', { defaultValue: 'Save draft' })}
              </Button>
              <Button
                variant="primary"
                onClick={() => void onFinalize()}
                loading={submitting === 'finalize'}
                disabled={
                  !scorable || !setsValid || winnerRegistrationId === null || submitting !== null
                }
                data-testid="match-score-finalize"
              >
                {t('admin:tournament.scoreDialog.finalize', { defaultValue: 'Finalize' })}
              </Button>
            </>
          ) : (
            <Button
              variant="destructive"
              onClick={() => void onCorrect()}
              loading={submitting === 'correct'}
              disabled={
                !scorable ||
                !setsValid ||
                winnerRegistrationId === null ||
                reason.trim().length === 0 ||
                submitting !== null
              }
              data-testid="match-score-correct"
            >
              {t('admin:tournament.scoreDialog.correct', { defaultValue: 'Correct score' })}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="match-score-dialog">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!scorable ? (
          <Banner tone="info">
            {t('admin:tournament.scoreDialog.notScorable', {
              defaultValue: 'This match has an open slot (bye / awaiting a prior round).',
            })}
          </Banner>
        ) : null}
        {isFinal ? (
          <Banner tone="warning">
            {t('admin:tournament.scoreDialog.correctionNote', {
              defaultValue:
                'This match is final. Corrections re-apply ratings and are audit-logged; they are forbidden once a downstream match is final.',
            })}
          </Banner>
        ) : null}

        {/* Per-set score rows */}
        <div className="flex flex-col gap-2" data-testid="match-score-sets">
          {sets.map((s, idx) => (
            <div key={idx} className="flex items-end gap-2">
              <FormField
                label={
                  idx === 0
                    ? t('admin:tournament.scoreDialog.setA', {
                        name: aName,
                        defaultValue: '{name}',
                      })
                    : ''
                }
              >
                <NumberInput
                  value={s.a}
                  min={0}
                  step={1}
                  onChange={(v) => updateSet(idx, 'a', Math.max(0, Math.round(Number(v) || 0)))}
                  data-testid={`match-score-set-${idx}-a`}
                  aria-label={`${aName} set ${idx + 1}`}
                />
              </FormField>
              <span className="pb-2 text-ink-3 text-[12px]">–</span>
              <FormField
                label={
                  idx === 0
                    ? t('admin:tournament.scoreDialog.setB', {
                        name: bName,
                        defaultValue: '{name}',
                      })
                    : ''
                }
              >
                <NumberInput
                  value={s.b}
                  min={0}
                  step={1}
                  onChange={(v) => updateSet(idx, 'b', Math.max(0, Math.round(Number(v) || 0)))}
                  data-testid={`match-score-set-${idx}-b`}
                  aria-label={`${bName} set ${idx + 1}`}
                />
              </FormField>
              {sets.length > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSets((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`match-score-remove-set-${idx}`}
                >
                  {t('admin:tournament.scoreDialog.removeSet', { defaultValue: 'Remove' })}
                </Button>
              ) : null}
            </div>
          ))}
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSets((prev) => [...prev, { a: 0, b: 0 }])}
              data-testid="match-score-add-set"
            >
              {t('admin:tournament.scoreDialog.addSet', { defaultValue: 'Add set' })}
            </Button>
          </div>
        </div>

        {/* Winner picker (finalize / correct) */}
        {scorable ? (
          <FormField
            label={t('admin:tournament.scoreDialog.winner', { defaultValue: 'Winner' })}
            required
          >
            <div
              className="flex gap-4"
              role="radiogroup"
              aria-label={t('admin:tournament.scoreDialog.winner', { defaultValue: 'Winner' })}
            >
              <label className="flex items-center gap-1.5 text-[13px] text-ink">
                <input
                  type="radio"
                  name="match-score-winner"
                  checked={winnerSide === 'a'}
                  onChange={() => setWinnerSide('a')}
                  data-testid="match-score-winner-a"
                />
                {aName}
              </label>
              <label className="flex items-center gap-1.5 text-[13px] text-ink">
                <input
                  type="radio"
                  name="match-score-winner"
                  checked={winnerSide === 'b'}
                  onChange={() => setWinnerSide('b')}
                  data-testid="match-score-winner-b"
                />
                {bName}
              </label>
            </div>
          </FormField>
        ) : null}

        {/* Walkover flags (finalize only) */}
        {!isFinal && scorable ? (
          <div className="flex flex-col gap-1.5">
            <Checkbox
              checked={aPresent}
              onChange={(e) => setAPresent(e.target.checked)}
              label={t('admin:tournament.scoreDialog.aPresent', {
                name: aName,
                defaultValue: '{name} present',
              })}
              data-testid="match-score-a-present"
            />
            <Checkbox
              checked={bPresent}
              onChange={(e) => setBPresent(e.target.checked)}
              label={t('admin:tournament.scoreDialog.bPresent', {
                name: bName,
                defaultValue: '{name} present',
              })}
              data-testid="match-score-b-present"
            />
          </div>
        ) : null}

        {/* Mandatory correction reason */}
        {isFinal ? (
          <FormField
            label={t('admin:tournament.scoreDialog.reasonLabel', { defaultValue: 'Reason' })}
            required
          >
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin:tournament.scoreDialog.reasonPlaceholder', {
                defaultValue: 'Why are you correcting this score?',
              })}
              rows={3}
              maxLength={500}
              data-testid="match-score-reason"
            />
          </FormField>
        ) : null}
      </div>
    </Modal>
  );
}

export default MatchScoreDialog;
